import {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useEffect,
  useRef,
  useState
} from 'react';

import {
  profilePhotosContainer,
  profilePhotosSecondaryContainer
} from '../styles.ts';

import { FileData } from '@/shared/interface/userRegistrationData.ts';
import ProfileController from '@/api/profile/profile.ts';
import { useProfileStore } from '@/store/useProfileStore.ts';
import { PhotoState } from '@/enums/apiEnums.ts';
import { useWebSocketStore } from '@/store/useWebSocketStore.ts';
import { catchErrors } from '@/utils/sentry-monitor.tsx';
import { PhotoSlot } from '@/components/ProfileData/ProfilePhotos/components/PhotoSlot.tsx';
import { PhotoCropper } from '@/components/ProfileData/ProfilePhotos/components/PhotoCropper/PhotoCropper.tsx';
import { PhotoKind, ServerPhotoV2 } from '@/shared/type/userDataFields.ts';
import { helpers } from '@/helpers.ts';

const MAX_PHOTO_SLOTS: number = 3;

type UserPhotos = {
  photos: FileData[];
};

export type UserPhotosProps = {
  photos: FileData[];
  updateFields: (fields: Partial<UserPhotos>) => void;
  isEdit?: boolean;
  isSaving?: boolean[];
  setIsSaving?: Dispatch<SetStateAction<boolean[]>>;
  isVideoUploading?: boolean;
};

export const ProfilePhotos = ({
  photos,
  updateFields,
  isEdit = false,
  isSaving,
  setIsSaving,
  isVideoUploading = false
}: UserPhotosProps) => {
  const { aboutMe, setAboutMe, updateProfile } = useProfileStore();
  const { lastEvents } = useWebSocketStore();

  const [progressStates, setProgressStates] = useState<number[]>([0, 0, 0]);
  const [deletePhotoStates, setDeletePhotoStates] = useState<boolean[]>([
    false,
    false,
    false
  ]);

  const [cropImage, setCropImage] = useState<string | null>(null);
  const [cropIndex, setCropIndex] = useState<number | null>(null);
  const [isCropOpen, setIsCropOpen] = useState(false);
  const isHandlingCropRef = useRef(false);

  const canDelete = (photo: FileData) => {
    if (isEdit && photo.file !== undefined) {
      return true;
    }

    if (
      isEdit &&
      photo.file === undefined &&
      photo.state !== undefined &&
      photo.state === PhotoState.STATE_BEING_MODERATED
    ) {
      return false;
    }

    const state = photo.state;
    return state !== PhotoState.STATE_BEING_MODERATED;
  };

  useEffect(() => {
    if (!isEdit) return;
    const storePhotos = aboutMe?.profile?.v2photos || [];
    const mappedStorePhotos = storePhotos.map((file) => ({
      file: undefined,
      url: {
        [PhotoKind.KIND_PRIMARY]: helpers.getPrimaryPhoto(file.variants) ?? '',
        [PhotoKind.KIND_THUMB]: helpers.getThumbPhoto(file.variants) ?? ''
      },
      hid: file.hid,
      state: file.state,
      thumbhash: helpers.getThumbHash(file.variants)
    }));

    const isEqual =
      mappedStorePhotos.length === photos.length &&
      mappedStorePhotos.every(
        (photo, index) =>
          photo.hid === photos[index]?.hid &&
          photo.state === photos[index]?.state
      );

    if (!isEqual) {
      updateFields({ photos: mappedStorePhotos });
    }

    return () => {
      lastEvents.photoStateChange = undefined;
    };
  }, [lastEvents.photoStateChange]);

  const handleFileChange = async (
    event: ChangeEvent<HTMLInputElement>,
    slotIndex: number
    // eslint-disable-next-line @typescript-eslint/require-await
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      setCropImage(reader.result as string);
      setCropIndex(slotIndex);
      setIsCropOpen(true);
    };
    reader.readAsDataURL(file);
  };

  const handleCroppedImage = async (blob: Blob) => {
    if (cropIndex === null || isHandlingCropRef.current) return;
    isHandlingCropRef.current = true;

    const file = new File([blob], 'cropped.jpg', { type: 'image/jpeg' });

    const syntheticEvent = {
      target: { files: [file] }
    } as unknown as ChangeEvent<HTMLInputElement>;

    const slotIndex = cropIndex;

    setIsCropOpen(false);
    setCropImage(null);
    setCropIndex(null);

    try {
      await uploadImage(syntheticEvent, slotIndex);
    } finally {
      isHandlingCropRef.current = false;
    }
  };

  const uploadImage = async (
    event: ChangeEvent<HTMLInputElement>,
    slotIndex: number
  ) => {
    try {
      setIsSaving?.((prev) => {
        const updated = [...prev];
        updated[slotIndex] = true;
        return updated;
      });

      const selectedFiles = event.target.files
        ? Array.from(event.target.files)
        : [];
      const imageFiles = selectedFiles.filter((file) =>
        file.type.startsWith('image/')
      );

      if (imageFiles.length === 0) {
        return;
      }

      const availableSlots = MAX_PHOTO_SLOTS - photos.length;
      const photosOrigin = imageFiles.slice(0, availableSlots);

      const filesForPreview = [
        ...photos,
        ...photosOrigin.map((file, i) => ({
          file,
          url: {
            [PhotoKind.KIND_PRIMARY]: URL.createObjectURL(file),
            [PhotoKind.KIND_THUMB]: URL.createObjectURL(file)
          },
          hid: i.toString()
        }))
      ];

      updateFields({ photos: filesForPreview });

      const blob = imageFiles[0];
      const mimeType = blob.type || 'image/jpeg';

      const { upload_url, photo_hid, photo_url } =
        await ProfileController.preparePhoto(mimeType, blob.size);

      await ProfileController.uploadToS3(
        upload_url,
        blob,
        mimeType,
        (progress) => {
          updateProgressAt(slotIndex, progress);
        }
      );

      ProfileController.completePhoto(photo_hid, mimeType, blob.size).catch(
        () => {}
      );

      const updatedPhotos = [...filesForPreview];
      updatedPhotos[slotIndex] = {
        file: isEdit ? undefined : updatedPhotos[slotIndex].file,
        url: {
          [PhotoKind.KIND_PRIMARY]: photo_url,
          [PhotoKind.KIND_THUMB]: photo_url
        },
        hid: photo_hid,
        state: isEdit
          ? PhotoState.STATE_BEING_MODERATED
          : PhotoState.STATE_CREATED
      };

      updateFields({ photos: updatedPhotos });
      updateProgressAt(slotIndex, 0);

      if (aboutMe) {
        const newServerPhoto: ServerPhotoV2 = {
          hid: photo_hid,
          state: isEdit
            ? PhotoState.STATE_BEING_MODERATED
            : PhotoState.STATE_CREATED,
          variants: [
            { url: photo_url, kind: PhotoKind.KIND_PRIMARY, thumbhash: '' },
            { url: photo_url, kind: PhotoKind.KIND_THUMB, thumbhash: '' }
          ]
        };
        setAboutMe({
          ...aboutMe,
          profile: {
            ...aboutMe.profile,
            v2photos: [...aboutMe.profile.v2photos, newServerPhoto]
          }
        });
      }
    } catch (e) {
      catchErrors(e, true);
    } finally {
      setIsSaving?.((prev) => {
        const updated = [...prev];
        updated[slotIndex] = false;
        return updated;
      });
    }
  };

  const handleDelete = async (index: number) => {
    try {
      setDeletePhotoStates((prev) => {
        const newLoadingStates = [...prev];
        newLoadingStates[index] = true;
        return newLoadingStates;
      });

      const photoHid = photos[index]?.hid;

      if (photoHid) {
        await ProfileController.deletePhoto(photoHid);
        await updateProfile(undefined, { withDelay: 1000 });

        const newPhotos = photos.filter((photo) => photo.hid !== photoHid);
        updateFields({ photos: newPhotos });
      }
    } catch (e) {
      catchErrors(e, true);
    } finally {
      setDeletePhotoStates((prev) => {
        const newLoadingStates = [...prev];
        newLoadingStates[index] = false;
        return newLoadingStates;
      });
    }
  };

  const updateProgressAt = (index: number, value: number) => {
    setProgressStates((prev) => {
      const updated = [...prev];
      updated[index] = value;
      return updated;
    });
  };

  return (
    <div style={profilePhotosContainer}>
      <PhotoSlot
        key={0}
        index={0}
        photo={photos[0]}
        isMain
        isEdit={isEdit}
        isSaving={isSaving}
        isOtherUploading={isVideoUploading}
        progress={progressStates[0]}
        isDeleting={deletePhotoStates[0]}
        onFileChange={handleFileChange}
        onDelete={handleDelete}
        canDelete={canDelete}
      />

      <div style={profilePhotosSecondaryContainer}>
        {[1, 2].map((index) => (
          <PhotoSlot
            key={index}
            index={index}
            photo={photos[index]}
            isSaving={isSaving}
            isEdit={isEdit}
            isOtherUploading={isVideoUploading}
            progress={progressStates[index]}
            isDeleting={deletePhotoStates[index]}
            onFileChange={handleFileChange}
            onDelete={handleDelete}
            canDelete={canDelete}
          />
        ))}
      </div>

      {isCropOpen && cropImage && (
        <PhotoCropper
          open={isCropOpen && Boolean(cropImage)}
          imageSrc={cropImage}
          onCancel={() => setIsCropOpen(false)}
          onConfirm={handleCroppedImage}
        />
      )}
    </div>
  );
};
